import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  BackButton,
  ButtonWithLoader,
  type ClassesType,
  type Constant,
  ErrorTooltip,
  type FieldProp,
  type GenderRestriction,
  type IdObject,
  LanguageUtils,
  PermissionArea,
  PuiCheckbox,
  PuiTextField,
  type Question,
  Text,
  TextWithTooltip,
  UnitUtils,
  type UnsavedQuestion,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'
import { UnitTypes } from '@pbt/pbt-ui-components/src/localization'

import AgeRangeSelector from '~/components/common/inputs/AgeRangeSelector'
import GenderRestrictionSelect from '~/components/common/inputs/gender/GenderRestrictionSelect'
import PuiSelectAll from '~/components/common/inputs/PuiSelectAll'
import QuantityInput from '~/components/common/inputs/QuantityInput'
import WeightRangeInput from '~/components/common/inputs/WeightRangeInput'
import PuiSwitch from '~/components/common/PuiSwitch'
import FeatureToggle from '~/constants/featureToggle'
import { getUnitsState } from '~/store/duck/settings'
import {
  getCRUDByArea,
  getCurrentBusinessIsOmniChannel,
} from '~/store/reducers/auth'
import {
  getCustomQuestionFrequencies,
  getEventType,
  getFeatureToggle,
  getSpecies,
} from '~/store/reducers/constants'
import { getQuestionsIsLoading } from '~/store/reducers/questions'
import type { DataHandleWithUnsavedChanges } from '~/types'
import { getConstantsList, isFieldValuesChanged } from '~/utils'
import useFieldsChanged from '~/utils/useFieldsChanged'
import useWorkflowTypes from '~/utils/useWorkflowTypes'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    radioGroup: {
      gridColumnGap: theme.spacing(4),
    },
    radioLabel: {
      fontSize: '1.6rem',
      color: theme.colors.secondaryText,
      marginLeft: theme.spacing(0.5),
    },
    radio: {
      padding: theme.spacing(0.5),
    },
    labelRoot: {
      margin: 0,
    },
    button: {
      width: 150,
    },
  }),
  { name: 'QuestionSettings' },
)

export interface QuestionSettingsProps {
  classes?: ClassesType<typeof useStyles>
  disabled?: boolean
  onBack?: () => void
  onChange?: () => void
  onProceed?: (question: Partial<UnsavedQuestion>) => void
  question?: Question
  view?: boolean
}

export interface QuestionSettingsHandle extends DataHandleWithUnsavedChanges {
  add: () => void
  get: (updatedQuestion?: Question) => Partial<UnsavedQuestion>
}

function isToggleEnabled(toggle: boolean, returnVal: any, elseVal: any) {
  return toggle ? returnVal : elseVal
}

function withFallback<T>(value: T, fallback: T): T {
  return value || fallback
}

const QuestionSettings = forwardRef<
  QuestionSettingsHandle,
  QuestionSettingsProps
>(function QuestionSettings(
  {
    classes: classesProp,
    question,
    view,
    onBack,
    onProceed,
    disabled,
    onChange = () => {},
  },
  ref,
) {
  const classes = useStyles({ classes: classesProp })
  const isLoading = useSelector(getQuestionsIsLoading)
  const EventType = useSelector(getEventType)
  const Species = useSelector(getSpecies)
  const WorkFlowTypes = useWorkflowTypes()
  const CustomQuestionFrequencies: Constant[] = useSelector(
    getCustomQuestionFrequencies,
  )
  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)
  const unitsState = useSelector(getUnitsState)
  const permissions = useSelector(getCRUDByArea(PermissionArea.BUSINESS))
  const { t } = useTranslation(['Admin', 'Common', 'Tooltips'])
  const isGroupQuestionsSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.GROUP_QUESTIONS_SHARING),
  )
  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )
  const isCvcPrefillQuestionnaireEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_PREFILL_QUESTIONNAIRE),
  )

  const isUpdateDisabled = disabled || !permissions.update

  const AppointmentEvent =
    Utils.findConstantByName('Appointment', EventType) || {}
  const DefaultFrequencyId = Utils.findConstantIdByName(
    'Ask every time',
    CustomQuestionFrequencies,
  )
  const DefaultWorkflowId = Utils.findConstantIdByName(
    'SOAP: History',
    WorkFlowTypes,
  )

  const { fields, validate, reset } = useFields(
    [
      ...isToggleEnabled(
        isGroupQuestionsSharingEnabled,
        [
          {
            name: 'active',
            label: t('Common:ACTIVE_ONE'),
            type: 'toggle' as const,
            initialValue: withFallback(question?.active, false),
          },
        ],
        [],
      ),
      {
        name: 'frequencyId',
        label: t('Common:CHOOSE_HOW_OFTEN'),
        validators: ['required'],
        initialValue: question?.frequencyId || DefaultFrequencyId,
      },
      {
        name: 'workflow',
        label: t('Common:CHOOSE_WHERE_QUESTION_IS_ASKED'),
        validators: ['required'],
        initialValue: withFallback(
          question?.workflowIds && question.workflowIds[0],
          DefaultWorkflowId,
        ),
      },
      {
        name: 'rank',
        label: t('Common:RANK'),
        initialValue: withFallback(question?.rank, 1),
      },
      {
        name: 'patientIsNew',
        label: t('Common:PATIENT_IS_NEW'),
        type: 'toggle',
        initialValue: withFallback(question?.patientIsNew, false),
      },
      {
        name: 'persistResponse',
        label: t('Common:PERSIST_RESPONSE'),
        type: 'toggle',
        initialValue: withFallback(question?.persistResponse, false),
      },
      {
        name: 'appointmentTypeIds',
        label: t('Common:APPOINTMENT_TYPES'),
        type: 'select',
        initialValue: getConstantsList(
          question?.appointmentTypeIds,
          AppointmentEvent.subTypes,
        ),
      },
      {
        name: 'species',
        label: t('Common:SPECIES'),
        type: 'select',
        initialValue: getConstantsList(question?.speciesIds, Species) || '',
      },
      {
        name: 'genderRestrictions',
        label: t('Common:GENDER'),
        type: 'select',
        initialValue: withFallback(question?.genderRestrictions, []),
      },
      { name: 'ageRangeMin', initialValue: question?.ageRangeMin ?? '' },
      {
        name: 'ageRangeMinUnitId',
        initialValue: question?.ageRangeMinUnitId || '',
      },
      { name: 'ageRangeMax', initialValue: question?.ageRangeMax ?? '' },
      {
        name: 'ageRangeMaxUnitId',
        initialValue: withFallback(question?.ageRangeMaxUnitId, ''),
      },
      {
        name: 'weightMin',
        initialValue: withFallback(
          UnitUtils.convertUnits(
            UnitTypes.WEIGHT,
            question?.weightMin,
            unitsState,
          ),
          '',
        ),
      },
      {
        name: 'weightMax',
        initialValue: withFallback(
          UnitUtils.convertUnits(
            UnitTypes.WEIGHT,
            question?.weightMax,
            unitsState,
          ),
          '',
        ),
      },
      ...isToggleEnabled(
        isOmnichannel,
        [
          {
            name: 'imageUrl',
            initialValue: withFallback(question?.imageUrl, ''),
            label: t('Common:IMAGE_URL'),
            validators: ['website'],
            messages: {
              website: t('Validations:ENTER_VALID_URL'),
            },
          } as FieldProp,
        ],
        [],
      ),
    ],
    false,
  )

  const {
    active,
    frequencyId,
    workflow,
    rank,
    patientIsNew,
    persistResponse,
    appointmentTypeIds,
    species,
    genderRestrictions,
    ageRangeMin,
    ageRangeMinUnitId,
    ageRangeMax,
    ageRangeMaxUnitId,
    weightMin,
    weightMax,
    imageUrl,
  } = fields

  useFieldsChanged(() => {
    onChange()
  }, fields)

  useEffect(() => {
    if (question) {
      reset()
    }
  }, [question, unitsState])

  const createQuestionObject = (questionData?: Question) => {
    const newQuestion: Partial<UnsavedQuestion> = {
      frequencyId: frequencyId.value,
      workflowIds: [workflow.value],
      patientIsNew: patientIsNew.value,
      ...(isCvcPrefillQuestionnaireEnabled
        ? { persistResponse: persistResponse.value }
        : {}),
      appointmentTypeIds: R.pluck(
        'id',
        (appointmentTypeIds.value || []) as IdObject[],
      ),
      speciesIds: R.pluck('id', species.value as IdObject[]) || [],
      genderRestrictions: (
        (genderRestrictions.value || []) as GenderRestriction[]
      ).map((restriction) =>
        R.pick(['genderId', 'spayedNeuteredStatusId'], restriction),
      ),
      ...(isGroupQuestionsSharingEnabled && active
        ? { active: active.value }
        : {}),
    }

    if (isOmnichannel) {
      newQuestion.imageUrl = imageUrl.value || null
    }

    if (Number.isInteger(ageRangeMin.value)) {
      newQuestion.ageRangeMin = ageRangeMin.value
      newQuestion.ageRangeMinUnitId = ageRangeMinUnitId.value
    }

    if (Number.isInteger(ageRangeMax.value)) {
      newQuestion.ageRangeMax = ageRangeMax.value
      newQuestion.ageRangeMaxUnitId = ageRangeMaxUnitId.value
    }

    if (Number.isInteger(rank.value)) {
      newQuestion.rank = rank.value
    }

    newQuestion.weightMin = UnitUtils.serializeWeightUnit({
      field: weightMin,
      unitsState,
      initialValue: Number(questionData?.weightMin),
      fallback: 0,
    })

    newQuestion.weightMax = UnitUtils.serializeWeightUnit({
      field: weightMax,
      unitsState,
      initialValue: Number(questionData?.weightMax),
      fallback: 0,
    })

    const createFollowUpQuestionUpdate = () => ({
      workflowIds: [workflow.value],
      frequencyId: frequencyId.value,
      active: isGroupQuestionsSharingEnabled ? active.value : true,
      patientIsNew: patientIsNew.value,
      ...(isCvcPrefillQuestionnaireEnabled
        ? { persistResponse: persistResponse.value }
        : {}),
    })

    const questionWithUpdatedFollowUps =
      questionData?.answers && isFollowUpQuestionsEnabled
        ? {
            ...questionData,
            answers: questionData.answers.map((answer) =>
              answer.followUpQuestion
                ? {
                    ...answer,
                    followUpQuestion: {
                      ...answer.followUpQuestion,
                      ...createFollowUpQuestionUpdate(),
                    },
                  }
                : answer,
            ),
          }
        : questionData

    return { ...questionWithUpdatedFollowUps, ...newQuestion }
  }

  const add = () => {
    if (onProceed && validate()) {
      const finalQuestion = createQuestionObject(question)
      onProceed(finalQuestion)
    }
  }

  useImperativeHandle(ref, () => ({
    validate,
    get: (updatedQuestion?: Question) =>
      createQuestionObject(updatedQuestion || question),
    hasUnsavedChanges: () => isFieldValuesChanged(fields),
    add,
  }))

  return (
    <Grid container className={classes.root} direction="column" pb={3} px={3}>
      {isGroupQuestionsSharingEnabled && active && (
        <Grid item pb={1}>
          <PuiSwitch field={active} label={active.label} name={active.name} />
        </Grid>
      )}
      <Grid item>
        <FormControl component="fieldset">
          <ErrorTooltip
            message={t('Tooltips:CHOOSE_FREQUENCY')}
            open={!frequencyId.valid}
            placement="top"
          >
            <FormLabel component="legend">
              <Text strong variant="subheading3">
                {frequencyId.label}*
              </Text>
            </FormLabel>
          </ErrorTooltip>
          <RadioGroup
            row
            aria-label={t('Common:FREQUENCY_OTHER')}
            className={classes.radioGroup}
            name="frequencies1"
            value={frequencyId.value}
            onChange={frequencyId.set}
          >
            {CustomQuestionFrequencies.map((frequency) => (
              <FormControlLabel
                classes={{
                  root: classes.labelRoot,
                  label: classes.radioLabel,
                }}
                control={
                  <Radio
                    className={classes.radio}
                    disabled={isUpdateDisabled}
                  />
                }
                key={frequency.id}
                label={LanguageUtils.getTranslatedFieldName(frequency)}
                value={frequency.id}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item ml={0.5} mt={1}>
        {isCvcPrefillQuestionnaireEnabled && (
          <TextWithTooltip
            tooltipText={t(
              'Tooltips:WHEN_ENABLED_PATIENT_ANSWERS_WILL_BE_PREFILLED',
            )}
            variant="body"
          >
            <PuiCheckbox
              disabled={isUpdateDisabled}
              field={persistResponse}
              label={persistResponse.label}
              labelProps={{ sx: { marginRight: 0 } }}
            />
          </TextWithTooltip>
        )}
      </Grid>
      <Grid item mt={1}>
        <FormControl component="fieldset">
          <ErrorTooltip
            message={t('Tooltips:CHOOSE_WORKFLOW')}
            open={frequencyId.valid && !workflow.valid}
            placement="top"
          >
            <FormLabel component="legend">
              <Text strong variant="subheading3">
                {workflow.label}
              </Text>
            </FormLabel>
          </ErrorTooltip>
          <RadioGroup
            row
            aria-label={t('Common:WORKFLOW_OTHER')}
            className={classes.radioGroup}
            name="workflows1"
            value={workflow.value}
            onChange={workflow.set}
          >
            {WorkFlowTypes.map((type) => (
              <FormControlLabel
                classes={{
                  root: classes.labelRoot,
                  label: classes.radioLabel,
                }}
                control={
                  <Radio
                    className={classes.radio}
                    disabled={isUpdateDisabled}
                  />
                }
                key={type.id}
                label={LanguageUtils.getTranslatedFieldName(type)}
                value={type.id}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item mt={2}>
        <TextWithTooltip
          strong
          tooltipText={t('Tooltips:QUESTIONS_APPEAR_ORDER')}
          variant="subheading3"
        >
          {rank.label}
        </TextWithTooltip>
        <QuantityInput
          showControls
          disabled={isUpdateDisabled}
          field={rank}
          max={1000}
          min={1}
        />
      </Grid>
      <Grid item mt={2}>
        <TextWithTooltip
          strong
          tooltipText={t(
            'Tooltips:IF_PATIENT_OR_APPOINTMENT_DOES_NOT_MEET_CRITERIA_QUESTION',
          )}
          variant="subheading3"
        >
          {t('Admin:CATALOG.QUESTION_SETTINGS.SHOW_QUESTION')}
        </TextWithTooltip>
      </Grid>
      <Grid item>
        <PuiCheckbox
          disabled={isUpdateDisabled}
          field={patientIsNew}
          label={patientIsNew.label}
        />
      </Grid>
      <PuiSelectAll
        disabled={isUpdateDisabled}
        field={appointmentTypeIds}
        items={AppointmentEvent.subTypes}
        label={appointmentTypeIds.label}
      />
      <Grid container item columnSpacing={2} mt={2}>
        <Grid item xs>
          <PuiSelectAll
            disabled={isUpdateDisabled}
            field={species}
            items={Species}
            label={species.label}
          />
        </Grid>
        <Grid item xs>
          <GenderRestrictionSelect
            disabled={isUpdateDisabled}
            field={genderRestrictions}
          />
        </Grid>
      </Grid>
      <Grid container item columnSpacing={2} mt={3}>
        <Grid container item alignItems="center" xs={5}>
          <Grid item>
            <Text display="inline" variant="body">
              {t('Common:AGE_RANGE_LABEL')}:
            </Text>
          </Grid>
          <Grid item xs ml={1}>
            <AgeRangeSelector
              fullWidth
              disabled={isUpdateDisabled}
              endUnit={ageRangeMaxUnitId.value}
              endValue={ageRangeMax.value}
              startUnit={ageRangeMinUnitId.value}
              startValue={ageRangeMin.value}
              onEndChange={ageRangeMax.setValue}
              onEndUnitChange={ageRangeMaxUnitId.setValue}
              onStartChange={ageRangeMin.setValue}
              onStartUnitChange={ageRangeMinUnitId.setValue}
            />
          </Grid>
        </Grid>
        <Grid container item alignItems="center" xs={7}>
          <WeightRangeInput
            disabled={isUpdateDisabled}
            maxWeightField={weightMax}
            minWeightField={weightMin}
          />
        </Grid>
        {isOmnichannel && (
          <Grid container item pt={2}>
            <TextWithTooltip strong variant="subheading3">
              {t('Common:IMAGE')}
            </TextWithTooltip>
            <Grid container item mt={-1}>
              <PuiTextField
                disabled={isUpdateDisabled}
                field={imageUrl}
                label={imageUrl.label}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      {!view && (
        <Grid container item columnSpacing={1.5} mt={2}>
          <Grid item>
            <BackButton label={t('Common:BACK_ACTION')} onClick={onBack} />
          </Grid>
          <Grid item>
            <ButtonWithLoader
              className={classes.button}
              disabled={isLoading}
              loading={isLoading}
              type="submit"
              onClick={add}
            >
              {t('Common:ADD_ACTION')}
            </ButtonWithLoader>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
})

export default QuestionSettings
